import { createStore, StoreApi } from 'zustand/vanilla';
import { format } from 'date-fns';
/*
  Imported from the defining modules rather than from the package's barrel: a module the barrel
  re-exports which imports the barrel back is a require cycle, and Metro warns that a cycle can hand
  you an uninitialised value.
*/
import type { Board, DomainUser as User, LoadedPlay, PlayCompletion, PlayData, SimpleDate } from '../types';
import type { TimerContextType } from '../providers/TimerProvider';
import {
  applyReverseMove,
  buildBoardPlayState,
  computeMove,
  hasSameArrayShape,
  isValidMoveAction,
  type GameConfig,
  type GamePlayActions,
  type GamePlayState,
} from './playState';

/**
 * The play store value: exactly `GamePlayState & GamePlayActions` (a snapshot can be handed straight
 * to PlayStateContext) plus the internal completion timestamp and the record*Used hooks. The store
 * concerns itself only with the evolving, persisted play data — cells, move history, win history, and
 * completion. The transient check/hint feedback (messages, hint box, the auto-check reminder) lives in
 * React state (useCheckHintState) that layers on top of this store.
 */
export type PlayStore<BoardT, CellT> = GamePlayState<BoardT, CellT> &
  GamePlayActions<BoardT, CellT> & {
    /** Internal: completion timestamp, folded into winsHistory on reset. Not part of GamePlayState. */
    completedAt: Date | null;
    /** Record that a Check / Hint was used, bumping the persisted playStats counters. Called by the
     * check/hint store, which owns the feedback UI but not the persisted play data. */
    recordCheckUsed: () => void;
    recordHintUsed: () => void;
    /** Record that the play was paused (and later resumed), bumping the persisted pauseCount. Called
     * from the provider when the injected timer transitions into the paused state. */
    recordPause: () => void;
    /** Replace all board-play state from another version of the play: one a sync brought from elsewhere
     * as the player returned to the board. Also re-syncs the injected timer. */
    replaceFromPlay: (play: LoadedPlay | null) => void;
  };

export interface CreatePlayStoreArgs<BoardT extends Board, CellT> {
  user: User;
  board: BoardT;
  /** Optional: only used to keep WeekBar / Archive caches in sync. Omit for slug/special boards. */
  boardDate?: SimpleDate;
  config: GameConfig<BoardT, CellT>;
  initialPlay: LoadedPlay | null;
  /** Injected timer API. Its functions are stable (ref-backed useCallbacks), so capturing once is safe. */
  timer: Pick<TimerContextType, 'getElapsedSeconds' | 'setElapsedSeconds' | 'start' | 'stop' | 'reset'>;
}

export function createPlayStore<BoardT extends Board, CellT>(
  args: CreatePlayStoreArgs<BoardT, CellT>
): StoreApi<PlayStore<BoardT, CellT>> {
  const { user, config, timer } = args;

  const effectiveBoardDate = (boardDate?: SimpleDate): SimpleDate =>
    boardDate ?? (format(new Date(), 'yyyy-MM-dd') as SimpleDate);

  return createStore<PlayStore<BoardT, CellT>>((set, get) => {
    // Builds a GamePlayState snapshot for passing to config callbacks (canBoardBeComplete).
    const fullPlayState = (playDataOverride?: PlayData<CellT>): GamePlayState<BoardT, CellT> => {
      const s = get();
      return {
        user,
        boardDate: s.boardDate,
        board: s.board,
        complete: s.complete,
        playDuration: s.playDuration,
        playData: playDataOverride ?? s.playData,
        winsHistory: s.winsHistory,
        moveHistory: s.moveHistory,
        currentPlayId: s.currentPlayId,
        justCompleted: s.justCompleted,
      };
    };

    const applyNewPlayData = (newPlayData: PlayData<CellT>) => {
      const { playData, moveHistory, board, completedAt } = get();
      const moveAction = computeMove(playData, newPlayData);
      const elapsed = timer.getElapsedSeconds();

      let newComplete = false;
      let newCompletedAt = completedAt;

      // Completion is play state: a move that fills the board correctly ends the play. The auto-check
      // reminder for a filled-but-wrong board is handled separately by the check/hint store.
      if (config.canBoardBeComplete(board, fullPlayState(newPlayData))) {
        const errors = config.checkBoard(newPlayData.cells, board);
        if (errors.length === 0) {
          timer.stop();
          newComplete = true;
          newCompletedAt = new Date();
          config.onCompletion(board, args.boardDate, user, newPlayData, elapsed);
        }
      }

      const newMoveHistory = moveAction.length > 0 ? [...(moveHistory || []), moveAction] : moveHistory;

      set({
        complete: newComplete,
        playDuration: elapsed,
        playData: newPlayData,
        moveHistory: newMoveHistory,
        completedAt: newCompletedAt,
        // Sticky for the life of this play: the solved screen mounts after the completing move, so it
        // can only distinguish a fresh solve from an opened one by asking the store.
        ...(newComplete && { justCompleted: true }),
      });
    };

    return {
      // ─── State ──────────────────────────────────────────────────────────────
      user,
      board: args.board,
      boardDate: effectiveBoardDate(args.boardDate),
      ...buildBoardPlayState(args.board, config, args.initialPlay),

      // ─── Actions ────────────────────────────────────────────────────────────
      setCell: (row, col, cell, derive) => {
        const { playData } = get();
        if (!playData) return;
        const newCells = playData.cells.map((r, ri) => (ri === row ? r.map((c, ci) => (ci === col ? cell : c)) : r));
        const next = { ...playData, cells: newCells };
        applyNewPlayData(derive ? derive(playData, next) : next);
        config.onMove?.(get().board, args.boardDate, { row, col, cell });
      },

      setCells: (cells, derive) => {
        const { playData } = get();
        if (!playData) return;
        const next = { ...playData, cells };
        applyNewPlayData(derive ? derive(playData, next) : next);
        config.onMove?.(get().board, args.boardDate);
      },

      applyMove: (update) => {
        const { playData } = get();
        if (!playData) return;
        const next = update(playData);
        if (next !== playData) applyNewPlayData(next);
      },

      handleUndo: () => {
        const { playData, moveHistory } = get();
        if (!playData || moveHistory.length <= 0) return;

        const lastMove = moveHistory[moveHistory.length - 1];
        if (!isValidMoveAction(lastMove)) {
          // Legacy plays stored moveHistory inside play_data; clear it there too.
          const cleanedPlayData = { ...playData, moveHistory: [] };
          set({ complete: false, playDuration: timer.getElapsedSeconds(), playData: cleanedPlayData });
          return;
        }

        const newHistory = moveHistory.slice(0, -1);
        const newPlayData = applyReverseMove(playData, lastMove);
        if (!hasSameArrayShape(newPlayData.cells, playData.cells)) return;

        set({
          complete: false,
          playDuration: timer.getElapsedSeconds(),
          playData: newPlayData,
          moveHistory: newHistory,
        });
      },

      handleReset: (options) => {
        const { playData, board, complete, winsHistory, playDuration, completedAt, moveHistory } = get();
        if (!playData) return;

        const initialState = config.getInitialCells(board);
        // Clearing an unfinished board is still the same attempt, so its counters (like its elapsed
        // time) carry over and the eventual completion reports every hint and check that was used.
        // Resetting a *completed* board starts a fresh attempt, whose counters begin at zero.
        const newPlayStats = complete ? config.initialPlayData.playStats : playData.playStats;
        const newPlayData = { ...config.initialPlayData, cells: initialState, playStats: newPlayStats };
        // An undoable reset is one more move, which Undo takes back like any other. A completed board's
        // reset starts a new attempt, which has nothing to go back to.
        const undoable = !!options?.undoable && !complete;
        const resetMove = undoable ? computeMove(playData, newPlayData) : [];
        const newWinsHistory = [...winsHistory];
        if (complete) {
          newWinsHistory.unshift({
            completionTime: playDuration,
            completionHintsUsed: playData.playStats.hintsUsed,
            completionChecksUsed: playData.playStats.checksUsed,
            completedAt: completedAt ?? new Date(),
          });
          timer.reset();
          timer.start(0);
          // Resetting a completed board starts a replay: a fresh attempt (never "resumed") whose
          // replay index is the number of prior completions, now including the one just folded in.
          config.onStart(board, args.boardDate, user, false, newWinsHistory.length);
        }

        set({
          moveHistory: undoable ? (resetMove.length > 0 ? [...moveHistory, resetMove] : moveHistory) : [],
          winsHistory: newWinsHistory,
          complete: false,
          playData: newPlayData,
          currentPlayId: null,
          justCompleted: false,
          ...(complete && { playDuration: 0, completedAt: null }),
        });
      },

      fillBoardToOneMoveAway: () => {
        const { playData, board, moveHistory } = get();
        if (!playData) return;
        const almostCompleteSolution = config.createAlmostCompleteState(board);
        const moveAction = computeMove(playData, { ...playData, cells: almostCompleteSolution });
        const newMoveHistory = moveAction.length > 0 ? [...moveHistory, moveAction] : moveHistory;
        set({ complete: false, playData: { ...playData, cells: almostCompleteSolution }, moveHistory: newMoveHistory });
      },

      setCurrentPlayId: (id) => set({ currentPlayId: id }),

      replaceFromPlay: (play) => {
        set({ ...buildBoardPlayState(get().board, config, play) });
        if (play?.complete) {
          timer.setElapsedSeconds(play.play_duration ?? 0);
          timer.stop();
        } else {
          timer.setElapsedSeconds(play?.play_duration ?? 0);
        }
      },

      recordCheckUsed: () =>
        set((s) => ({
          playData: {
            ...s.playData,
            playStats: { ...s.playData.playStats, checksUsed: s.playData.playStats.checksUsed + 1 },
          },
        })),
      recordHintUsed: () =>
        set((s) => ({
          playData: {
            ...s.playData,
            playStats: { ...s.playData.playStats, hintsUsed: s.playData.playStats.hintsUsed + 1 },
          },
        })),
      recordPause: () =>
        set((s) => ({
          playData: {
            ...s.playData,
            playStats: { ...s.playData.playStats, pauseCount: (s.playData.playStats.pauseCount ?? 0) + 1 },
          },
        })),
    };
  });
}
